/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid engine/table.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import { constructTable, type FilterFn } from '@tanstack/table-core'
import { evaluateColumnFilter } from '../filters'
import { isElementRTL } from '../util/direction'
import type {
 DataGridColumn, DataGridColumnFilterValue, DataGridFeatures, DataGridItem, DataGridOptions,
 DataGridTable, DataGridTableState
} from '../types'
import { isGlobalFilterEnabled } from '../ui/toolbar'
import { assertFeatureCoverage } from './validate'

const UNPAGINATED_PAGE_SIZE = Number.MAX_SAFE_INTEGER
const INFINITE_SCROLL_PAGE_SIZE = 50

const toTanStackFilterFn = (column: DataGridColumn): FilterFn<DataGridFeatures, DataGridItem> => {
  const { filterFn } = column
  if (filterFn) {
    return (row, columnId, filterValue) => filterFn(row.getValue(columnId), filterValue, row.original)
  }

  return (row, columnId, filterValue) =>
    evaluateColumnFilter(row.getValue(columnId), filterValue as DataGridColumnFilterValue, column.filterType)
}

export const createGridTable = (
  options: DataGridOptions,
  getElement?: () => Element | null
): DataGridTable => {
  const sortingOptions = typeof options.sorting === 'object' ? options.sorting : {}
  const sizingOptions = typeof options.columnSizing === 'object' ? options.columnSizing : {}
  const paginationOptions = typeof options.pagination === 'object' ? options.pagination : {}
  const serverSide = Boolean(options.dataProvider)
  const sizingEnabled = Boolean(options.columnSizing)
  const pinningEnabled = Boolean(options.columnPinning)
  // Pinning offsets come from column.getSize(); sticky positioning is only
  // pixel-correct when the rendered widths equal those sizes, so both features
  // seed and apply explicit widths.
  const widthsEnabled = sizingEnabled || pinningEnabled
  const paginated = Boolean(options.pagination) || serverSide
  const infiniteOptions = typeof options.infiniteScroll === 'object' ? options.infiniteScroll : {}

  if (options.infiniteScroll) {
    if (!serverSide) {
      throw new TypeError('DATA-GRID: Option "infiniteScroll" requires "dataProvider".')
    }

    if (options.pagination) {
      throw new TypeError('DATA-GRID: Option "infiniteScroll" cannot be combined with "pagination".')
    }

    if (!options.virtualization) {
      throw new TypeError('DATA-GRID: Option "infiniteScroll" requires "virtualization".')
    }
  }

  // The default set comes from the ENTRY's DataGrid subclass, never from a
  // static import here - that split is what keeps the full preset out of the
  // lite bundle. A custom set is typed as the full DataGridFeatures; internals
  // stay on the full alias and capability-guard every optional-feature call.
  const { features } = options
  if (!features) {
    throw new TypeError('DATA-GRID: Option "features" is required.')
  }

  assertFeatureCoverage('data-grid', features, {
    cellSelection: options.cellSelection,
    columnFilters: options.columnFilters,
    columnOrder: Boolean(options.columnOrder),
    columnPinning: pinningEnabled,
    columnSizing: sizingEnabled,
    columnVisibility: Boolean(options.columnVisibility),
    globalFilter: isGlobalFilterEnabled(options),
    pagination: paginated,
    rowSelection: Boolean(options.rowSelection),
    selectFilters: options.columnFilters && options.columns.some(column => column.filterType === 'select'),
    sorting: Boolean(options.sorting)
  })

  const initialState: Partial<DataGridTableState> = {
    // An unpaginated grid is one page holding every row - but only when the
    // pagination feature is registered at all.
    ...('rowPaginationFeature' in features && {
      pagination: {
        pageIndex: 0,
        pageSize: options.infiniteScroll ?
          infiniteOptions.pageSize ?? INFINITE_SCROLL_PAGE_SIZE :
          (paginated ? paginationOptions.pageSize ?? 10 : UNPAGINATED_PAGE_SIZE)
      }
    }),
    ...(typeof options.columnPinning === 'object' && {
      columnPinning: { start: [], end: [], ...options.columnPinning }
    }),
    ...(Array.isArray(options.columnOrder) && { columnOrder: options.columnOrder }),
    ...(typeof options.columnVisibility === 'object' && { columnVisibility: options.columnVisibility })
  }

  return constructTable<DataGridFeatures, DataGridItem>({
    columns: options.columns.map(column => {
      const size = column.width ?? null
      return {
        accessorKey: column.key,
        enableColumnFilter: column.filterable !== false,
        enableHiding: column.hideable !== false,
        enableResizing: column.resizable !== false,
        enableSorting: column.sortable !== false,
        filterFn: toTanStackFilterFn(column),
        id: column.key,
        meta: { label: column.label, formatter: column.formatter, width: column.width },
        ...(widthsEnabled && size !== null && { size })
      }
    }),
    columnResizeMode: sizingOptions.mode ?? 'onChange',
    // Construction-time only — table-core copies the options object, so a
    // getter here would never be re-read. A direction flipped later is picked
    // up by refreshing this through setOptions when a resize starts.
    columnResizeDirection: isElementRTL(getElement?.()) ? 'rtl' : 'ltr',
    data: serverSide ? [] : options.items,
    enableCellSelection: Boolean(options.cellSelection),
    enableColumnFilters: Boolean(options.columnFilters),
    enableColumnPinning: pinningEnabled,
    enableColumnResizing: sizingEnabled,
    enableGlobalFilter: isGlobalFilterEnabled(options),
    enableHiding: Boolean(options.columnVisibility),
    enableMultiSort: sortingOptions.multiple !== false,
    enableRowSelection: Boolean(options.rowSelection),
    enableSorting: Boolean(options.sorting),
    enableSortingRemoval: Boolean(sortingOptions.resetable),
    features,
    globalFilterFn: 'includesString',
    ...(serverSide && {
      manualFiltering: true,
      manualPagination: true,
      manualSorting: true,
      rowCount: 0
    }),
    ...(options.itemKey && { getRowId: options.itemKey }),
    initialState,
    renderFallbackValue: null
  })
}

// table.store emits the new snapshot only (and only when a slice identity
// changed - it compares shallowly), so the previous one is kept here for the
// slice-by-slice diffing the grid does on every state change.
export const subscribeToTableState = (
  table: DataGridTable,
  listener: (previous: DataGridTableState, next: DataGridTableState) => void
): (() => void) => {
  let previous = table.store.state
  const subscription = table.store.subscribe(next => {
    const from = previous
    previous = next
    listener(from, next)
  })

  return () => subscription.unsubscribe()
}
